import authModule from "../../../utilities/AuthModule";
import { axiosRetryPost, localApiInstance } from "../../../utilities/AxiosRetryUtil";
import { CacheHelper } from "../../../utilities/CacheHelper";
import { logger } from "../../../utilities/logger";
import { DatasourceTypeEnum } from "../components/enums/datasourceTypeEnum";
import { IDatasourceRequestDto } from "../interfaces/DatasourceRequestDto";
import { IDatasourceQueryDto } from "../interfaces/IDatasourceQueryDto";

interface ICacheDatasourceData {
    key: string;
    data?: any[];
}

export class DatasourceService {
    GetData = async (request: IDatasourceQueryDto): Promise<any[]> => {
        try {
            let cachedData: ICacheDatasourceData;
            if (request.allowCache) {
                cachedData = this.getCachedData(request);
                if (Array.isArray(cachedData?.data)) {
                    return cachedData.data;
                }
            }

            if (request.typeId === DatasourceTypeEnum.SharePoint) {
                request.accessToken = await authModule.getSharePointAccessToken();
            }
            const result = await axiosRetryPost("Datasource/GetData", request, undefined, false, localApiInstance);

            const dsData = result?.data ?? [];
            if (request.allowCache) {
                CacheHelper.set(cachedData.key, dsData);
            }
            return dsData;
        } catch (err) {
            logger(err, "DatasourceService.ts (GetData)");
            return [];
        }
    }

    GetStructure = async (request: IDatasourceRequestDto): Promise<any[]> => {
        try {
            let cachedData: ICacheDatasourceData;
            if (request.allowCache) {
                cachedData = this.getCachedData(request, true);
                if (Array.isArray(cachedData?.data)) {
                    return cachedData.data;
                }
            }

            if (request.typeId === DatasourceTypeEnum.SharePoint) {
                request.accessToken = await authModule.getSharePointAccessToken();
            }
            const result = await axiosRetryPost("Datasource/GetStructure", request, undefined, false, localApiInstance);
            // return result?.data ?? [];
            const dsData = result?.data ?? [];
            if (request.allowCache) {
                CacheHelper.set(cachedData.key, dsData);
            }
            return dsData;
        } catch (err) {
            logger(err, "DatasourceService.ts (GetStructure)");
            return [];
        }
    }

    GetEditStructure = async (request: IDatasourceRequestDto): Promise<any[]> => {
        try {
            if (request.typeId === DatasourceTypeEnum.SharePoint) {
                request.accessToken = await authModule.getSharePointAccessToken();
            }
            const result = await axiosRetryPost("Datasource/GetEditStructure", request, undefined, false, localApiInstance);
            return result?.data ?? [];
        } catch (err) {
            logger(err, "DatasourceService.ts (GetEditStructure)");
            return [];
        }
    }

    Search = async (request: IDatasourceQueryDto): Promise<any[]> => {
        try {
            let cachedData: ICacheDatasourceData;
            if (request.allowCache) {
                cachedData = this.getCachedData(request);
                if (Array.isArray(cachedData?.data)) {
                    if (request.isStrict) {
                        const filteredData = cachedData.data.filter(data => request.queryParams.every(searchParam => {
                            const searchProperty: string = searchParam.name?.trim().toLowerCase();
                            for (let property in data) {
                                if (data.hasOwnProperty(property) && property.trim().toLowerCase() == searchProperty) {
                                    return data[property]?.trim().toLowerCase() == searchParam.value?.trim().toLowerCase();
                                }
                            }
                            return false;
                        }));
                        return filteredData;
                    }
                    else {
                        const filteredData = cachedData.data.filter(data => request.queryParams.some(searchParam => {
                            const searchProperty: string = searchParam.name.toLowerCase();
                            for (let property in data) {
                                if (data.hasOwnProperty(property) && property.toLowerCase() == searchProperty) {
                                    return data[property]?.trim().toLowerCase() == searchParam.value?.trim().toLowerCase();
                                }
                            }
                            return false;
                        }));
                        return filteredData;
                    }
                }
            }

            if (request.typeId === DatasourceTypeEnum.SharePoint) {
                request.accessToken = await authModule.getSharePointAccessToken();
            }
            const result = await axiosRetryPost("Datasource/Search", request, undefined, false, localApiInstance);
            return result?.data ?? [];
        } catch (err) {
            logger(err, "DatasourceService.ts (Search)");
            return [];
        }
    }

    private getCachedData = (request: IDatasourceRequestDto, structureOnly: boolean = false): ICacheDatasourceData => {
        let cacheKey: string = `${request.tenantId}.ds_data.${request.id}`;
        if (structureOnly) {
            cacheKey += ".structure";
        }
        if (request.allowCache) {
            const cachedData: any[] = CacheHelper.get(cacheKey);
            if (Array.isArray(cachedData)) {
                return { key: cacheKey, data: cachedData };
            }
        }
        return { key: cacheKey };
    }
}
